import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, CheckCircle, Shield, Users, Calendar, Mail, 
  BarChart3, Music, FileText, Zap, Globe, Lock
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: Users,
      title: 'CRM & Lead Management',
      description: 'Manage your contacts, leads, and customer relationships with powerful organization tools and custom categories.'
    },
    {
      icon: Mail,
      title: 'Email & Communications',
      description: 'Connect your Gmail or email accounts to manage all communications in one place with full inbox integration.'
    },
    {
      icon: Calendar,
      title: 'Tasks & Calendar',
      description: 'Stay organized with comprehensive task management, scheduling, and calendar synchronization via Google Calendar.'
    },
    {
      icon: Music,
      title: 'Media Portfolio & Beat Store',
      description: 'Upload, manage, and sell your audio content with a professional beat store and media portfolio.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reports',
      description: 'Track your business performance with detailed analytics, financial reports, and actionable insights.'
    },
    {
      icon: Zap,
      title: 'AI Assistant',
      description: 'Boost productivity with AI-powered tools for content generation, analysis, and task automation.'
    }
  ];

  const dataUsageReasons = [
    {
      icon: Mail,
      title: 'Email Access (Gmail API)',
      reason: 'To display, send, and manage your emails directly within CREATRVERSE, providing a unified inbox experience without switching between apps.'
    },
    {
      icon: Calendar,
      title: 'Calendar Access (Google Calendar)',
      reason: 'To sync your events, schedule tasks, and manage appointments seamlessly within the CREATRVERSE dashboard.'
    },
    {
      icon: Users,
      title: 'Contact Information',
      reason: 'To import and sync your contacts for CRM functionality, enabling efficient lead management and communication tracking.'
    },
    {
      icon: Lock,
      title: 'Authentication (Google Sign-In)',
      reason: 'To provide secure, password-free login using your Google account, ensuring your data is protected with industry-standard security.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <span className="text-xl font-bold">CREATRVERSE</span>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/privacy" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-400 hover:text-white transition-colors text-sm"
              >
                Terms
              </Link>
              <Link 
                to="/auth" 
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-sm font-medium"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            Your All-in-One Business Dashboard
          </h1>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            CREATRVERSE is a comprehensive business management platform that helps creators, entrepreneurs, 
            and professionals manage their contacts, communications, tasks, media, and more — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/auth" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors font-medium"
            >
              Get Started <ArrowRight size={18} />
            </Link>
            <a 
              href="#features" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/20 hover:border-white/40 rounded-lg transition-colors font-medium"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Powerful Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Everything you need to manage your business, all in one unified platform.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-black border border-white/10 rounded-xl hover:border-purple-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-purple-600/20 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Usage Transparency Section */}
      <section id="data-usage" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-600/20 text-green-400 rounded-full mb-4">
              <Shield size={18} />
              <span className="text-sm font-medium">Data Transparency</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why We Request Your Data</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We believe in full transparency. Here's exactly why CREATRVERSE requests access to your data 
              and how we use it to provide you with the best experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {dataUsageReasons.map((item, index) => (
              <div 
                key={index}
                className="p-6 bg-white/5 border border-white/10 rounded-xl"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.reason}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 p-6 bg-purple-600/10 border border-purple-500/30 rounded-xl">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Our Commitment to Your Privacy</h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li>• We never sell your personal data to third parties</li>
                  <li>• Your data is encrypted in transit and at rest</li>
                  <li>• You can revoke access and delete your data at any time</li>
                  <li>• We only access the minimum data required to provide our services</li>
                  <li>• We comply with GDPR, CCPA, and other data protection regulations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Built with Security in Mind</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="w-14 h-14 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="font-semibold mb-2">Secure Authentication</h3>
              <p className="text-gray-400 text-sm">Industry-standard OAuth 2.0 for secure Google sign-in</p>
            </div>
            <div className="p-6">
              <div className="w-14 h-14 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="font-semibold mb-2">Data Encryption</h3>
              <p className="text-gray-400 text-sm">All data encrypted with TLS/SSL in transit and AES-256 at rest</p>
            </div>
            <div className="p-6">
              <div className="w-14 h-14 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="font-semibold mb-2">Compliance</h3>
              <p className="text-gray-400 text-sm">GDPR and CCPA compliant data handling practices</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-8">
            Join CREATRVERSE today and take control of your business operations.
          </p>
          <Link 
            to="/auth" 
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors font-medium text-lg"
          >
            Create Your Account <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">C</span>
              </div>
              <span className="font-bold">CREATRVERSE</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <a href="mailto:darren@creatrverse.com" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} CREATRVERSE. All rights reserved.</p>
            <p className="mt-2">
              <a href="https://operatr.me" className="hover:text-white transition-colors">https://operatr.me</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
